import React from 'react';
import { useNavigate } from 'react-router-dom';

const HowToPlay = ({ isModal = false, onClose = null }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/system');
    }
  };

  const containerClass = isModal 
    ? "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    : "min-h-screen text-white p-8";

  const contentClass = isModal
    ? "bg-gray-800 rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto text-white"
    : "max-w-6xl mx-auto";

  return (
    <div className={containerClass}>
      <div className={contentClass}>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-cyan-400">🎮 White Rabbit Game Guide</h1>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="space-y-8">
          {/* Getting Started */}
          <section>
            <h2 className="text-2xl font-bold text-green-400 mb-4">🚀 Getting Started</h2>
            <div className="bg-gray-800 border border-green-400 p-6 rounded-lg space-y-3">
              <p><strong>💻 Deploy Computer:</strong> Click anywhere on the map to set your location (remember to zoom in to deploy)</p>
              <p><strong>🔄 Movement Tip:</strong> After moving your computer, it's good practice to click "Move Refresh" next to the map to update all features and users in your sniffer tool</p>
              <p><strong>🖱️ Activate Sniffer:</strong> Click your computer to reveal nearby targets (5km radius)</p>
              <p><strong>📡 Sniffer reveals:</strong> Treasure chests, banks, casinos, ATMs, vaults, users, houses, drones</p>
              <p><strong>🚁 Deploy Drones:</strong> Purchase drones from Parts for strategic operations</p>
              <p><strong>🍔 Movement System:</strong> Every time you move your computer, you consume 1 food/drink supply</p>
              <p><strong>🛒 Supply Management:</strong> Buy more supplies from shops or produce them at your own farm</p>
            </div>
          </section>

          {/* Hacking Rules */}
          <section>
            <h2 className="text-2xl font-bold text-red-400 mb-4">🔒 Important Hacking Rules</h2>
            <div className="bg-gray-800 border border-red-400 p-6 rounded-lg">
              <p className="text-red-300 font-bold mb-3">⚠️ CRITICAL: Only computers can hack financial targets!</p>
              <div className="space-y-2">
                <p><strong>🎰 Casino/Bank/ATM/Vault Hacking:</strong> Requires deployed computer within range</p>
                <p><strong>🚁 Drone Types & Functions:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li><strong>🔍 Scout Drones:</strong> Reconnaissance and sniffer detection (3km radius)</li>
                  <li><strong>⚔️ Assault Drones:</strong> Attack defense towers (30-minute battles)</li>
                  <li><strong>💥 Nuke Drones:</strong> Destroy command centers (20-minute attacks, 5km range)</li>
                </ul>
                <p><strong>💻 Computers:</strong> Required for all financial hacking operations</p>
              </div>
            </div>
          </section>

          {/* Treasure Hunting */}
          <section>
            <h2 className="text-2xl font-bold text-yellow-400 mb-4">🎯 Treasure Hunting</h2>
            <div className="bg-gray-800 border border-yellow-400 p-6 rounded-lg space-y-3">
              <p><strong>🎁 Treasure Chests:</strong> Find combinations for WR Coins</p>
              <p><strong>⭐ XP Chests:</strong> Instant experience (🌟 = Rare XP)</p>
              <p><strong>🏧 ATM codes:</strong> Found in treasure chests, stored in your personal vault</p>
            </div>
          </section>

          {/* Financial Systems */}
          <section>
            <h2 className="text-2xl font-bold text-blue-400 mb-4">💰 Financial Systems</h2>
            <div className="bg-gray-800 border border-blue-400 p-6 rounded-lg space-y-3">
              <p><strong>🏦 Banks:</strong> Brute force attack to breach bank security systems</p>
              <p><strong>🎰 Casinos:</strong> Hack slot machines and vaults for massive rewards</p>
              <p><strong>🏧 ATMs:</strong> Use codes or brute force to withdraw funds</p>
              <p><strong>🔐 Personal Vaults:</strong> Store ATM codes and WR Coins, hackable by others for rewards</p>
              <p><strong>💳 Savings Accounts:</strong> Earn 5% APY on stored funds (can be hacked with scanning tools)</p>
            </div>
          </section>

          {/* User Types */}
          <section>
            <h2 className="text-2xl font-bold text-purple-400 mb-4">👥 User Types</h2>
            <div className="bg-gray-800 border border-purple-400 p-6 rounded-lg space-y-3">
              <p><strong>⚡ Operating:</strong> Active users with deployed computers</p>
              <p><strong>🔒 Jailed:</strong> Users caught by SWAT, temporarily restricted</p>
              <p><strong>🤵 Field Agent:</strong> Special status users with enhanced abilities</p>
            </div>
          </section>

          {/* Compound Defense */}
          <section>
            <h2 className="text-2xl font-bold text-orange-400 mb-4">🚐 Compound Defense</h2>
            <div className="bg-gray-800 border border-orange-400 p-6 rounded-lg space-y-3">
              <p><strong>🏗️ Build fortified bases:</strong> Command centers, defense towers, and radar systems</p>
              <p><strong>📡 Radar Detection:</strong> 10km radius threat detection and alerts</p>
              <p><strong>🎯 Defense Towers:</strong> Automatically defend against incoming attacks</p>
              <p><strong>⚡ Command Centers:</strong> Central control for your defensive network</p>
              <p><strong>🛡️ Protection:</strong> Shields your assets from enemy hacking attempts</p>
            </div>
          </section>

          {/* Supply System */}
          <section>
            <h2 className="text-2xl font-bold text-amber-400 mb-4">🍔 Supply System</h2>
            <div className="bg-gray-800 border border-amber-400 p-6 rounded-lg space-y-3">
              <p><strong>🚶 Movement Cost:</strong> Each computer move consumes 1 food/drink supply</p>
              <p><strong>🛒 Purchasing Supplies:</strong> Buy supplies from shops (businesses on the map)</p>
              <p><strong>🚜 Farm Production:</strong> Own a farm to produce your own supplies automatically</p>
              <p><strong>📊 Supply Limit:</strong> Maximum 10 supplies can be carried at once</p>
              <p><strong>⚠️ No Supplies = No Movement:</strong> Computer cannot move without adequate supplies</p>
            </div>
          </section>

          {/* Real Estate */}
          <section>
            <h2 className="text-2xl font-bold text-indigo-400 mb-4">🏠 Real Estate & Business</h2>
            <div className="bg-gray-800 border border-indigo-400 p-6 rounded-lg space-y-3">
              <p><strong>🏠 Houses:</strong> Purchase from Lifestyle, place on map, rent to others for passive income</p>
              <p><strong>🏢 Businesses:</strong> Buy businesses and link them together to create supply chains that sell to other users</p>
              <p><strong>💼 Management:</strong> Monitor your properties</p>
              <p><strong>💼 Job System:</strong> Get hired at businesses and work 4 days a week for passive income</p>
              <p><strong>💰 Employment Benefits:</strong> Steady income stream from working at offices and businesses</p>
            </div>
          </section>

          {/* SWAT System */}
          <section>
            <h2 className="text-2xl font-bold text-red-400 mb-4">🚨 SWAT System</h2>
            <div className="bg-gray-800 border border-red-400 p-6 rounded-lg space-y-3">
              <p><strong>⚠️ Warning:</strong> SWAT may respond to illegal activities!</p>
              <p><strong>🎯 Triggers:</strong> Successful hacks have a chance to alert authorities</p>
              <p><strong>🔒 Consequences:</strong> Getting caught results in temporary jail time</p>
              <p><strong>🚨 Report Attackers:</strong> If someone hacks you, report them to SWAT for immediate response</p>
              <p><strong>📋 SWAT Reports:</strong> Use the SWAT system to call authorities on criminals targeting you</p>
            </div>
          </section>

          {/* Tips & Strategy */}
          <section>
            <h2 className="text-2xl font-bold text-cyan-400 mb-4">💡 Tips & Strategy</h2>
            <div className="bg-gray-800 border border-cyan-400 p-6 rounded-lg space-y-3">
              <p><strong>🛡️ Build defenses:</strong> Protect your assets with compound defense systems</p>
              <p><strong>💰 Diversify income:</strong> Balance treasure hunting, hacking, and real estate</p>
              <p><strong>⚡ Stay mobile:</strong> Move your computer strategically to access different targets</p>
              <p><strong>🤝 Form alliances:</strong> Work with other players for mutual protection</p>
              <p><strong>🍔 Manage supplies:</strong> Keep food/drink supplies stocked for computer movement</p>
            </div>
          </section>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mt-8 pt-6 border-t border-gray-600">
          <button
            onClick={handleClose}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
          >
            {isModal ? 'Close' : 'Back to System'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowToPlay;